/**
 * lib/examPdf.ts
 * ------------------------------------------------------------------
 * Exam-running PDFs (first draft): the class subject/teacher table, the
 * per-subject exam register (invigilator ticks present/absent), and a
 * rough room seating plan + per-room register. All use the shared LKC
 * letterhead/footer chrome and the ink-light table conventions.
 * ------------------------------------------------------------------
 */
import { jsPDF } from 'jspdf';
import autoTable, {
  type CellHookData,
  type RowInput,
} from 'jspdf-autotable';
import {
  PDF,
  CONTENT_TOP,
  pageGeom,
  drawLetterhead,
  stampFooters,
} from '@/lib/pdfChrome';

/* ==================================================================
   1. CLASS SUBJECTS & TEACHERS  (per class: who does what, taught by whom)
   ================================================================== */
export interface ClassSubjectsRow {
  no: number;
  name: string;
  adm: string;
  subjects: { subject: string; teacher: string }[];
}
export interface ClassSubjectsPdfData {
  className: string;
  metaLine?: string;
  rows: ClassSubjectsRow[];
  logoDataUrl?: string | null;
  generatedBy?: string;
}

export function buildClassSubjectsPdf(data: ClassSubjectsPdfData): jsPDF {
  const geom = pageGeom('l');
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });

  const body: RowInput[] = data.rows.map((r) => [
    String(r.no),
    r.name,
    r.adm || '—',
    r.subjects.length
      ? r.subjects.map((s) => `${s.subject} — ${s.teacher || 'unassigned'}`).join('    ·    ')
      : '— no subjects on record —',
  ]);

  autoTable(pdf, {
    head: [['#', 'Student', 'Adm.', 'Subjects — Teacher']],
    body,
    startY: CONTENT_TOP,
    margin: { top: CONTENT_TOP, left: geom.MX, right: geom.MX, bottom: 14 },
    theme: 'plain',
    styles: {
      font: 'helvetica', fontSize: 8, textColor: PDF.INK,
      cellPadding: { top: 1.6, right: 2, bottom: 1.6, left: 2 },
      lineColor: PDF.RULE, lineWidth: { bottom: 0.1, top: 0, left: 0, right: 0 },
      overflow: 'linebreak', valign: 'top',
    },
    headStyles: {
      fillColor: PDF.BAND, textColor: PDF.NAVY, fontStyle: 'bold', fontSize: 7.5,
      halign: 'left', lineColor: PDF.NAVY, lineWidth: { bottom: 0.4, top: 0, left: 0, right: 0 },
    },
    columnStyles: {
      0: { cellWidth: 9, halign: 'center', textColor: PDF.MUTED },
      1: { cellWidth: 46, fontStyle: 'bold' },
      2: { cellWidth: 20, halign: 'center', textColor: PDF.MUTED },
      3: { cellWidth: 'auto' },
    },
    didParseCell: (h: CellHookData) => {
      if (h.section === 'body' && h.row.index % 2 === 1) h.cell.styles.fillColor = PDF.ZEBRA;
    },
    didDrawPage: () => drawLetterhead(pdf, {
      title: 'CLASS SUBJECTS & TEACHERS', metaLine: data.metaLine ?? data.className,
      logoDataUrl: null, geom,
    }),
  });

  stampFooters(pdf, { geom, generatedBy: data.generatedBy });
  return pdf;
}

/* ==================================================================
   2. EXAM REGISTER  (per form + subject: candidates + present/absent ticks)
   ================================================================== */
export interface ExamRegisterRow {
  no: number;
  name: string;
  adm: string;
  className: string;
}
export interface ExamRegisterPdfData {
  subjectName: string;
  formLabel: string;
  metaLine?: string;
  rows: ExamRegisterRow[];
  logoDataUrl?: string | null;
  generatedBy?: string;
}

/** Small empty tick-box drawn in the middle of a body cell. */
function drawTickBox(pdf: jsPDF, h: CellHookData) {
  const { x, y, width, height } = h.cell;
  const s = 3.6;
  pdf.setDrawColor(...PDF.MUTED);
  pdf.setLineWidth(0.25);
  pdf.rect(x + width / 2 - s / 2, y + height / 2 - s / 2, s, s, 'S');
}

export function buildExamRegisterPdf(data: ExamRegisterPdfData): jsPDF {
  const geom = pageGeom('p');
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const { MX, right } = geom;

  const head = [['#', 'Candidate', 'Adm.', 'Class', 'Present', 'Absent', 'Signature']];
  const presentCol = 4;
  const absentCol = 5;
  const body: RowInput[] = data.rows.map((r) => [
    String(r.no), r.name, r.adm || '—', r.className, '', '', '',
  ]);

  // Info band with blank invigilator/room/date fields drawn once under the
  // letterhead; the table starts below it.
  const infoY = CONTENT_TOP;
  const startY = infoY + 12;

  autoTable(pdf, {
    head, body,
    startY,
    margin: { top: startY, left: MX, right: MX, bottom: 14 },
    theme: 'plain',
    styles: {
      font: 'helvetica', fontSize: 8.5, textColor: PDF.INK,
      cellPadding: { top: 2, right: 2, bottom: 2, left: 2 },
      lineColor: PDF.RULE, lineWidth: { bottom: 0.1, top: 0, left: 0, right: 0 },
      overflow: 'ellipsize', valign: 'middle',
    },
    headStyles: {
      fillColor: PDF.BAND, textColor: PDF.NAVY, fontStyle: 'bold', fontSize: 7.5,
      halign: 'center', lineColor: PDF.NAVY, lineWidth: { bottom: 0.4, top: 0, left: 0, right: 0 },
    },
    columnStyles: {
      0: { cellWidth: 9, halign: 'center', textColor: PDF.MUTED },
      1: { cellWidth: 'auto', fontStyle: 'bold' },
      2: { cellWidth: 20, halign: 'center', textColor: PDF.MUTED },
      3: { cellWidth: 20, halign: 'center', textColor: PDF.MUTED },
      4: { cellWidth: 20, halign: 'center' },
      5: { cellWidth: 20, halign: 'center' },
      6: { cellWidth: 34 },
    },
    didParseCell: (h: CellHookData) => {
      if (h.section === 'body' && h.row.index % 2 === 1 &&
          h.column.index !== presentCol && h.column.index !== absentCol) {
        h.cell.styles.fillColor = PDF.ZEBRA;
      }
    },
    didDrawCell: (h: CellHookData) => {
      if (h.section === 'body' && (h.column.index === presentCol || h.column.index === absentCol)) {
        drawTickBox(pdf, h);
      }
    },
    didDrawPage: () => {
      drawLetterhead(pdf, {
        title: 'EXAM REGISTER',
        metaLine: data.metaLine ?? `${data.formLabel} · ${data.subjectName} · ${data.rows.length} candidates`,
        logoDataUrl: null, geom,
      });
      // Blank invigilator / room / date fields.
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8.5);
      pdf.setTextColor(...PDF.INK);
      const half = (right - MX) / 2;
      pdf.text('Invigilator: ______________________________', MX, infoY + 4);
      pdf.text('Room: ____________________', MX + half, infoY + 4);
      pdf.text('Date: ____________________', MX, infoY + 10);
      pdf.text('Started: __________   Ended: __________', MX + half, infoY + 10);
    },
  });

  stampFooters(pdf, { geom, generatedBy: data.generatedBy });
  return pdf;
}

/* ==================================================================
   3. ROOM SEATING PLAN + ROOM REGISTER  (mixed forms/subjects) — draft
   ================================================================== */
export interface SeatSlot { name: string; sub: string; } // sub = "Form · Subject"
export interface SeatingRoomPlan { roomName: string; cols: number; seats: SeatSlot[]; }
export interface SeatingPlanPdfData {
  metaLine?: string;
  rooms: SeatingRoomPlan[];
  logoDataUrl?: string | null;
  generatedBy?: string;
}

export function buildSeatingPlanPdf(data: SeatingPlanPdfData): jsPDF {
  const geom = pageGeom('l');
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
  const { MX, right, PH } = geom;
  const contentW = right - MX;

  data.rooms.forEach((room, ri) => {
    if (ri > 0) pdf.addPage();
    drawLetterhead(pdf, {
      title: `SEATING PLAN — ${room.roomName}`,
      metaLine: data.metaLine ?? `${room.seats.length} candidates`,
      logoDataUrl: null, geom,
    });

    const cols = Math.max(1, room.cols);
    const rows = Math.ceil(room.seats.length / cols) || 1;
    const gap = 3;
    const cellW = (contentW - gap * (cols - 1)) / cols;
    const availH = PH - CONTENT_TOP - 16;
    const cellH = Math.min(24, Math.max(14, (availH - gap * (rows - 1)) / rows));
    let y = CONTENT_TOP;

    room.seats.forEach((seat, i) => {
      const c = i % cols;
      const r = Math.floor(i / cols);
      const x = MX + c * (cellW + gap);
      const cy = y + r * (cellH + gap);
      pdf.setDrawColor(...PDF.RULE);
      pdf.setLineWidth(0.3);
      pdf.setFillColor(...PDF.ZEBRA);
      pdf.roundedRect(x, cy, cellW, cellH, 1.2, 1.2, 'FD');
      // seat number
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(6.5);
      pdf.setTextColor(...PDF.MUTED);
      pdf.text(`Seat ${i + 1}`, x + 2, cy + 4);
      // student name
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8.5);
      pdf.setTextColor(...PDF.INK);
      pdf.text(pdf.splitTextToSize(seat.name, cellW - 4) as string[], x + 2, cy + 9);
      // form · subject
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(6.8);
      pdf.setTextColor(...PDF.NAVY);
      pdf.text(pdf.splitTextToSize(seat.sub, cellW - 4) as string[], x + 2, cy + cellH - 3);
    });
  });

  if (data.rooms.length === 0) {
    drawLetterhead(pdf, { title: 'SEATING PLAN', metaLine: data.metaLine, logoDataUrl: null, geom });
  }
  stampFooters(pdf, { geom, generatedBy: data.generatedBy });
  return pdf;
}

export interface RoomRegisterStudent {
  seat: number;
  name: string;
  adm: string;
  info: string; // "Form · Subject"
}
export interface RoomRegisterPdfData {
  metaLine?: string;
  rooms: { roomName: string; students: RoomRegisterStudent[] }[];
  logoDataUrl?: string | null;
  generatedBy?: string;
}

export function buildRoomRegisterPdf(data: RoomRegisterPdfData): jsPDF {
  const geom = pageGeom('p');
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });

  const head = [['Seat', 'Candidate', 'Adm.', 'Form · Subject', 'Present', 'Absent']];
  const presentCol = 4;
  const absentCol = 5;

  data.rooms.forEach((room, ri) => {
    const body: RowInput[] = room.students.map((s) => [
      String(s.seat), s.name, s.adm || '—', s.info, '', '',
    ]);
    autoTable(pdf, {
      head, body,
      startY: ri === 0 ? CONTENT_TOP : undefined,
      margin: { top: CONTENT_TOP, left: geom.MX, right: geom.MX, bottom: 14 },
      pageBreak: ri === 0 ? 'auto' : 'always',
      theme: 'plain',
      styles: {
        font: 'helvetica', fontSize: 8.5, textColor: PDF.INK,
        cellPadding: { top: 2, right: 2, bottom: 2, left: 2 },
        lineColor: PDF.RULE, lineWidth: { bottom: 0.1, top: 0, left: 0, right: 0 },
        overflow: 'ellipsize', valign: 'middle',
      },
      headStyles: {
        fillColor: PDF.BAND, textColor: PDF.NAVY, fontStyle: 'bold', fontSize: 7.5,
        halign: 'center', lineColor: PDF.NAVY, lineWidth: { bottom: 0.4, top: 0, left: 0, right: 0 },
      },
      columnStyles: {
        0: { cellWidth: 12, halign: 'center', textColor: PDF.MUTED },
        1: { cellWidth: 'auto', fontStyle: 'bold' },
        2: { cellWidth: 20, halign: 'center', textColor: PDF.MUTED },
        3: { cellWidth: 46, textColor: PDF.NAVY },
        4: { cellWidth: 20, halign: 'center' },
        5: { cellWidth: 20, halign: 'center' },
      },
      didParseCell: (h: CellHookData) => {
        if (h.section === 'body' && h.row.index % 2 === 1 &&
            h.column.index !== presentCol && h.column.index !== absentCol) {
          h.cell.styles.fillColor = PDF.ZEBRA;
        }
      },
      didDrawCell: (h: CellHookData) => {
        if (h.section === 'body' && (h.column.index === presentCol || h.column.index === absentCol)) {
          drawTickBox(pdf, h);
        }
      },
      didDrawPage: () => drawLetterhead(pdf, {
        title: `ROOM REGISTER — ${room.roomName}`,
        metaLine: data.metaLine ?? `${room.students.length} candidates`,
        logoDataUrl: null, geom,
      }),
    });
  });

  stampFooters(pdf, { geom, generatedBy: data.generatedBy });
  return pdf;
}
